export interface ContainerConfig {
  typeColumns?: number;
  columnsXs?: number;
  columnsSm?: number;
  columnsMd?: number;
  columnsLg?: number;
  rowGutter?: string;
  layoutClass?: string;
  offsetXs?: number;
  offsetMd?: number;
}

export type ConfigFunction = (typeColumns?: number) => ContainerConfig;

const defaultConfig: ConfigFunction = () => ({
  typeColumns: undefined,
  columnsXs: 1,
  columnsSm: undefined,
  columnsMd: undefined,
  columnsLg: undefined,
  rowGutter: 'none',
  layoutClass: '',
  offset: 0,
  offsetXs: undefined,
  offsetMd: undefined,
});

const articleCardContainer: ConfigFunction = (typeColumns: number) => {
  switch (typeColumns) {
    case 2:
      return {
        columnsXs: 1,
        columnsSm: 2,
        rowGutter: 'md',
      };
    case 3:
      return {
        columnsXs: 1,
        columnsSm: 3,
        rowGutter: 'md',
      };
    case 4:
      return {
        columnsXs: 1,
        columnsSm: 2,
        columnsMd: 4,
        rowGutter: 'md',
      };
    default:
      return { columnsXs: 1 };
  }
};

const personCardContainer: ConfigFunction = (typeColumns: number) => ({
  columnsXs: 1,
  columnsSm: 3,
  columnsMd: typeColumns === 4 ? 4 : 3,
  columnsLg: typeColumns,
  rowGutter: 'md',
});

const callToActionCardContainer: ConfigFunction = (typeColumns: number) => {
  switch (typeColumns) {
    case 2:
      return {
        columnsXs: 1,
        rowGutter: 'md',
        columnsSm: 2,
      };
    case 3:
      return {
        columnsXs: 1,
        rowGutter: 'md',
        columnsSm: 3,
      };
    case 4:
      return {
        columnsXs: 1,
        rowGutter: 'md',
        columnsSm: 2,
        columnsMd: 4,
      };
    case 8:
      return {
        columnsXs: 1,
        rowGutter: 'md',
        columnsSm: 2,
        layoutClass: 'eight-four',
      };
    default:
      return {};
  }
};

const iconCardHorizontal: ConfigFunction = (typeColumns: number) => {
  switch (typeColumns) {
    case 2:
      return {
        columnsXs: 1,
        columnsSm: 2,
        rowGutter: 'md',
      };
    default:
      return {
        columnsXs: 1,
        offsetMd: 1,
        rowGutter: 'md',
      };
  }
};

const imageCardHorizontal: ConfigFunction = () => ({
  columnsXs: 1,
  columnsMd: 2,
  rowGutter: 'md',
});

const imageCardVertical: ConfigFunction = (typeColumns: number) => {
  switch (typeColumns) {
    case 2:
      return {
        columnsXs: 1,
        columnsSm: 2,
        rowGutter: 'md',
      };
    case 3:
      return {
        columnsXs: 1,
        columnsSm: 3,
        rowGutter: 'md',
      };
    case 4:
      return {
        columnsXs: 1,
        columnsSm: 2,
        columnsMd: 4,
        rowGutter: 'md',
      };
    default:
      return {
        columnsXs: 1,
        rowGutter: 'md',
      };
  }
};

const textCard: ConfigFunction = (typeColumns: number) => {
  switch (typeColumns) {
    case 2:
      return {
        columnsXs: 1,
        columnsSm: 2,
        offsetXs: 0,
        offsetSm: 0,
        offsetMd: 0,
        offsetLg: 0,
        rowGutter: 'md',
      };
    case 3:
      return {
        columnsXs: 1,
        columnsSm: 3,
        offsetXs: 0,
        offsetSm: 0,
        offsetMd: 0,
        offsetLg: 0,
        rowGutter: 'md',
      };
    case 4:
      return {
        columnsXs: 1,
        columnsSm: 2,
        columnsMd: 4,
        offsetXs: 0,
        offsetSm: 0,
        offsetMd: 0,
        offsetLg: 0,
        rowGutter: 'md',
      };
    default:
      return {
        columnsXs: 1,
        offsetXs: 0,
        offsetSm: 1,
        offsetMd: 1,
        offsetLg: 1,
        rowGutter: 'md',
      };
  }
};

const mediaCaptionContainer: ConfigFunction = (typeColumns: number) => {
  switch (typeColumns) {
    case 2:
      return {
        columnsXs: 1,
        rowGutter: 'md',
        columnsSm: 2,
      };
    case 3:
      return {
        columnsXs: 1,
        rowGutter: 'md',
        columnsSm: 3,
      };
    case 4:
      return {
        columnsXs: 1,
        rowGutter: 'md',
        columnsSm: 2,
        columnsMd: 4,
      };
    default:
      return {
        columnsXs: 1,
        rowGutter: 'md',
      };
  }
};

const variableContentBlockHorizontal: ConfigFunction = () => ({
  columnsXs: 1,
  offsetMd: 1,
  rowGutter: 'lg',
});

const variableContentBlockVertical: ConfigFunction = (typeColumns: number) => {
  switch (typeColumns) {
    case 2:
      return {
        columnsXs: 1,
        columnsSm: 2,
        rowGutter: 'lg',
      };
    case 3:
      return {
        columnsXs: 1,
        columnsSm: 3,
        rowGutter: 'lg',
      };
    case 4:
      return {
        columnsXs: 1,
        columnsSm: 2,
        columnsMd: 4,
        rowGutter: 'lg',
      };
    default:
      return {
        columnsXs: 1,
        rowGutter: 'lg',
      };
  }
};

const eightFour: ConfigFunction = () => ({
  columnsXs: 1,
  columnsSm: 2,
  layoutClass: 'eight-four',
});

const spanMode: ConfigFunction = () => ({
  columnsXs: 12,
});

const tenOne: ConfigFunction = () => ({
  columnsXs: 1,
  offsetXs: 0,
  offsetMd: 1,
});

const configs = {
  'defaults': defaultConfig,
  'article-card': articleCardContainer,
  'call-to-action-card': callToActionCardContainer,
  'icon-card-horizontal': iconCardHorizontal,
  'image-card-horizontal': imageCardHorizontal,
  'image-card-vertical': imageCardVertical,
  'text-card': textCard,
  'media-caption': mediaCaptionContainer,
  'person-card': personCardContainer,
  'variable-content-block-horizontal': variableContentBlockHorizontal,
  'variable-content-block-vertical': variableContentBlockVertical,
  'eight-four': eightFour,
  'ten-one': tenOne,
  'span-mode': spanMode,
};

export type ContainerType = keyof typeof configs;

export default configs;
